import { api, getUser, clearSession } from "../lib/api";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bell, LogOut } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

export function BrandMark({ size = "sm" }) {
  if (size === "lg") {
    return (
      <div className="flex flex-col items-center gap-3" data-testid="brand-hero">
        <div
          className="flex items-center justify-center text-white font-semibold"
          style={{
            width: 64, height: 64, borderRadius: 18,
            background: "var(--cs-primary)",
            boxShadow: "0 8px 20px rgba(124,179,66,0.35)",
            fontSize: 24, letterSpacing: "-0.5px",
          }}
        >CS</div>
        <div className="text-center">
          <div className="font-semibold tracking-tight" style={{ fontSize: 32, letterSpacing: "-0.6px" }}>ContracepSur</div>
          <div className="text-[14px]" style={{ color: "var(--cs-text-muted)" }}>
            Plateforme de référence MI–OB pour une contraception sécurisée
          </div>
        </div>
      </div>
    );
  }
  return (
    <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
      <div className="flex items-center justify-center text-white font-semibold"
           style={{ width: 32, height: 32, borderRadius: 10, background: "var(--cs-primary)", fontSize: 13 }}>
        CS
      </div>
      <div className="leading-tight">
        <div className="font-semibold text-[15px]" style={{ letterSpacing: "-0.3px" }}>ContracepSur</div>
        <div className="text-[11px]" style={{ color: "var(--cs-text-muted)" }}>Référence MI ↔ OB-GYN</div>
      </div>
    </Link>
  );
}

export function BrandBar({ children }) {
  const user = getUser();
  const navigate = useNavigate();
  const loc = useLocation();
  const queryClient = useQueryClient();

  const { data: unread } = useQuery({
    queryKey: ["unread-count"],
    queryFn: async () => (await api.get("/events/unread-count")).data.count,
    refetchInterval: 20000,
    enabled: !!user,
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["unread-count"] });
  }, [loc.pathname]);

  const homeFor = (role) => role === "im" ? "/im" : role === "obgyn" ? "/obgyn" : "/admin";

  return (
    <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b" style={{ borderColor: "var(--cs-border)" }}>
      <div className="max-w-5xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {user ? (
            <Link to={homeFor(user.role)} className="flex items-center gap-2.5" data-testid="brand-link">
              <div className="flex items-center justify-center text-white font-semibold"
                   style={{ width: 32, height: 32, borderRadius: 10, background: "var(--cs-primary)", fontSize: 13 }}>
                CS
              </div>
              <div className="leading-tight">
                <div className="font-semibold text-[15px]" style={{ letterSpacing: "-0.3px" }}>ContracepSur</div>
                <div className="text-[11px]" style={{ color: "var(--cs-text-muted)" }}>Référence MI ↔ OB-GYN</div>
              </div>
            </Link>
          ) : <BrandMark />}
          {children}
        </div>
        <div className="flex items-center gap-2">
          {user && (
            <>
              <button
                onClick={() => navigate("/inbox")}
                className="relative p-2 rounded-full hover:bg-[var(--cs-bg)] transition"
                data-testid="bell-button"
                aria-label="Notifications"
              >
                <Bell size={20} strokeWidth={1.8} color="var(--cs-text)" />
                {unread > 0 && (
                  <span
                    data-testid="unread-badge"
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold text-white flex items-center justify-center"
                    style={{ background: "var(--cs-danger)" }}
                  >{unread > 99 ? "99+" : unread}</span>
                )}
              </button>
              <button
                onClick={() => { clearSession(); navigate("/"); }}
                className="p-2 rounded-full hover:bg-[var(--cs-bg)] transition"
                data-testid="logout-button"
                aria-label="Déconnexion"
              >
                <LogOut size={18} strokeWidth={1.8} color="var(--cs-text-muted)" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function Footer() {
  return (
    <div
      className="text-center text-[11px] uppercase py-8"
      style={{ letterSpacing: "0.6px", color: "var(--cs-text-subtle)" }}
      data-testid="footer-disclaimer"
    >
      Données dé-identifiées · Aucune information patient stockée
    </div>
  );
}

export function Chip({ tone = "muted", children, ...rest }) {
  const tones = {
    muted: { bg: "var(--cs-bg)", color: "var(--cs-text-muted)", border: "var(--cs-border)" },
    primary: { bg: "rgba(124,179,66,0.10)", color: "var(--cs-primary-dark)", border: "rgba(124,179,66,0.25)" },
    accent: { bg: "rgba(245,158,11,0.10)", color: "var(--cs-accent)", border: "rgba(245,158,11,0.25)" },
    danger: { bg: "rgba(220,38,38,0.10)", color: "var(--cs-danger)", border: "rgba(220,38,38,0.25)" },
    neutral: { bg: "var(--cs-bg)", color: "var(--cs-text)", border: "var(--cs-border)" },
  };
  const t = tones[tone];
  return (
    <span className="cs-chip" style={{ background: t.bg, color: t.color, borderColor: t.border }} {...rest}>
      {children}
    </span>
  );
}

export function UrgencyChip({ value }) {
  const map = { routine: ["muted","Routine"], urgent: ["accent","Urgent"], very_urgent: ["danger","Très urgent"] };
  const [tone, label] = map[value] || ["muted","—"];
  return <Chip tone={tone} data-testid={`urgency-chip-${value}`}>{label}</Chip>;
}

export function StatusChip({ value }) {
  const map = {
    pending: ["accent","En attente"],
    scheduled: ["primary","Planifié"],
    seen: ["primary","Vue"],
    missed: ["danger","Manqué"],
    completed: ["neutral","Terminé"],
    need_info: ["muted","Info requise"],
  };
  const [tone, label] = map[value] || ["muted", value || "—"];
  return <Chip tone={tone} data-testid={`status-chip-${value}`}>{label}</Chip>;
}

export function Page({ children }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--cs-bg)" }}>
      <BrandBar />
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 md:px-6 py-6 md:py-10 cs-fade">
        {children}
      </main>
      <Footer />
    </div>
  );
}

export function Spinner() {
  return (
    <div
      className="inline-block w-5 h-5 border-2 rounded-full animate-spin"
      style={{ borderColor: "var(--cs-border)", borderTopColor: "var(--cs-primary)" }}
    />
  );
}

export function EmptyState({ title, hint, action }) {
  return (
    <div className="cs-card p-10 text-center cs-fade" data-testid="empty-state">
      <div className="text-[17px] font-semibold tracking-tight" style={{ letterSpacing: "-0.3px" }}>{title}</div>
      {hint && <div className="text-[14px] mt-1" style={{ color: "var(--cs-text-muted)" }}>{hint}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
